import { useQuery } from '@tanstack/react-query'
import { type ReactNode } from 'react'
import { useNavigate } from 'react-router'

import {
  fetchHospitalSpecialists,
  hospitalSpecialistsKey,
  type HospitalSpecialist,
} from '../../api/specialists'
import { formatPesewas } from '../../lib/money'
import { Icon } from '../Icon'

/** Group a flat roster by specialty, preserving first-seen order. */
function groupBySpecialty(
  people: HospitalSpecialist[],
): { specialty: string; people: HospitalSpecialist[] }[] {
  const groups: { specialty: string; people: HospitalSpecialist[] }[] = []
  for (const p of people) {
    let g = groups.find((x) => x.specialty === p.specialty)
    if (!g) {
      g = { specialty: p.specialty, people: [] }
      groups.push(g)
    }
    g.people.push(p)
  }
  return groups
}

/**
 * Specialists tab (M12-T03) — the hospital's roster grouped by specialty. Each
 * row opens the specialist's profile (M14-T10; previously a dead `<li>`) and
 * shows the hospital-admin `availability_note` for this hospital when set — so
 * the tab answers "who is here, and when".
 *
 * NO LONGER READ-ONLY (M33-T05, ADR-0062). A symptom search ended here with two
 * actions, "Call hospital" and "Directions", while
 * `specialists/:specialistSlug/request/:hospitalSlug` had existed since M14 and
 * nothing routed to it. Destination 1 is "a named doctor, at a named hospital,
 * with a book action", so each row carries one.
 *
 * `specialty` is the slug M33-T04 carried in from the search. When set, the tab
 * shows only the doctors who answer it — the patient asked about one thing.
 */
export function SpecialistsTab({
  slug,
  specialty = null,
}: {
  slug: string
  specialty?: string | null
}): ReactNode {
  const navigate = useNavigate()
  const query = useQuery({
    queryKey: hospitalSpecialistsKey(slug),
    queryFn: ({ signal }) => fetchHospitalSpecialists(slug, signal),
  })

  if (query.isPending) {
    return (
      <p className="px-4 py-6 text-[13px] text-ink-3" role="status">
        Loading specialists…
      </p>
    )
  }
  if (query.isError) {
    return (
      <p className="px-4 py-6 text-[13px] text-ink-3" role="alert">
        Couldn&apos;t load specialists.
      </p>
    )
  }
  const shown =
    specialty === null
      ? query.data
      : query.data.filter((s) => s.specialty_slug === specialty)

  // ORDER MATTERS. An empty roster used to win over the carried specialty, so a
  // symptom arrival at a hospital with no doctors got "No specialists listed"
  // and nothing to do — while the footer was demoted at the same time, leaving
  // the page with no action at all. ADR-0062: no search returns nothing.
  if (specialty === null && query.data.length === 0) {
    return (
      <p className="px-4 py-6 text-[13px] text-ink-3">
        No specialists listed for this hospital.
      </p>
    )
  }

  // UNDERSTOOD, AND NOBODY HERE. Never an empty list: the patient was sent here
  // by a specialty this hospital turns out not to staff, and an empty tab reads
  // as "we did not understand you" (ADR-0047). M33-T06 puts a general
  // practitioner behind this sentence.
  if (specialty !== null && shown.length === 0) {
    return (
      <div className="px-4 py-6">
        <p className="text-[13px] text-ink-2">
          No doctor here covers what you asked about.
        </p>
        <button
          type="button"
          onClick={() =>
            navigate(`/specialists?specialty=${encodeURIComponent(specialty)}`)
          }
          className="mt-2 inline-flex items-center gap-1.5 rounded-r2 border border-accent bg-accent-bg px-3 py-2 text-[13px] font-medium text-accent-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          See who does, elsewhere <Icon name="chevr" size={14} />
        </button>
      </div>
    )
  }

  return (
    <div className="px-4 py-4">
      {groupBySpecialty(shown).map((g) => (
        <section key={g.specialty} className="mb-4">
          {/* h2, not h3: the page's only other heading is the hospital name as
            `h1`, and skipping a level is an axe `heading-order` violation. It
            predates this ticket and was invisible because the axe tests render
            the Beds tab; M33-T05 makes this the landing state of every symptom
            journey. */}
          <h2 className="mb-1.5 font-mono text-[10px] uppercase tracking-wide text-ink-3">
            {g.specialty}
          </h2>
          <ul className="divide-y divide-line-1">
            {g.people.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/specialists/${s.slug}`)}
                  aria-label={`${s.full_name}, ${s.specialty} — view profile`}
                  className="flex w-full items-center gap-3 py-2.5 text-left hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-bg-sunken text-ink-3">
                    <Icon name="user" size={16} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium text-ink-1">
                      {s.full_name}
                    </div>
                    <div className="mt-0.5 text-[11px] text-ink-3">
                      {s.languages.join(' · ')}
                      {s.consult_fee_pesewas != null && (
                        <>
                          {' · consult '}
                          <span className="tabular-nums">
                            {formatPesewas(s.consult_fee_pesewas)}
                          </span>
                        </>
                      )}
                    </div>
                    {s.availability_note && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-ink-2">
                        <Icon name="clock" size={11} /> {s.availability_note}
                      </div>
                    )}
                  </div>
                  <Icon
                    name="chevr"
                    size={14}
                    className="shrink-0 text-ink-3"
                  />
                </button>
                {/* THE BOOK ACTION (M33-T05, ADR-0062). It targets the route
                  M14 built and nothing used. Outside the row's button because
                  a button inside a button is not valid HTML and the inner one
                  is unreachable by keyboard. */}
                <div className="pb-2.5">
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/specialists/${s.slug}/request/${slug}`)
                    }
                    aria-label={`Book ${s.full_name}`}
                    className="ml-12 inline-flex items-center gap-1.5 rounded-r2 bg-accent px-3 py-1.5 text-[13px] font-medium text-accent-on focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                  >
                    Book <Icon name="chevr" size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}
